import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import {
  holidayTypes,
  HOLIDAY_TYPE_META,
  HOLIDAY_DESCRIPTION_MAX_LENGTH,
} from '@/constants/holidays';

/** Blank row shape used while parsing an uploaded holidays CSV/Excel file. */
export const makeEmptyHolidayRow = () => ({
  name: '',
  date: '',
  type: '',
  description: '',
});

/** Recognized column header spellings when parsing an uploaded file. */
const HOLIDAY_HEADER_ALIASES = {
  name: 'name',
  'holiday name': 'name',
  holiday: 'name',
  date: 'date',
  type: 'type',
  'holiday type': 'type',
  description: 'description',
  desc: 'description',
  note: 'description',
  notes: 'description',
};

const normalizeHolidayType = (value) => {
  const trimmed = String(value || '')
    .trim()
    .toLowerCase();
  if (!trimmed) return holidayTypes.public; // matches the Add Holiday modal's default
  if (
    trimmed === holidayTypes.optional ||
    trimmed === HOLIDAY_TYPE_META[holidayTypes.optional].label.toLowerCase()
  ) {
    return holidayTypes.optional;
  }
  if (
    trimmed === holidayTypes.public ||
    trimmed === HOLIDAY_TYPE_META[holidayTypes.public].label.toLowerCase()
  ) {
    return holidayTypes.public;
  }
  return null;
};

/**
 * Row-level validation for parsed holiday rows. Returns each row with an
 * `_errors` array (flat messages, for the shared CSV preview table) and a
 * `_fieldErrors` map, mirroring `validateCsvRows` in `employeeImport.js`.
 * `existingDates` flags rows that collide with holidays already saved.
 */
export const validateHolidayRows = (rows, existingDates = []) => {
  const existingDateSet = new Set(existingDates);
  const seenDatesInFile = new Set();

  return rows.map((row) => {
    const errors = [];
    const fieldErrors = {};

    if (!row.name?.trim()) {
      errors.push('Holiday name is required');
      fieldErrors.name = 'Required';
    }

    let normalizedDate = row.date;
    if (!row.date?.trim()) {
      errors.push('Date is required');
      fieldErrors.date = 'Required';
    } else {
      const parsed = dayjs(row.date.trim());
      if (!parsed.isValid()) {
        errors.push('Invalid date format');
        fieldErrors.date = 'Invalid';
      } else {
        normalizedDate = parsed.format('YYYY-MM-DD');
        if (existingDateSet.has(normalizedDate)) {
          errors.push('A holiday already exists on this date');
          fieldErrors.date = 'Already exists';
        } else if (seenDatesInFile.has(normalizedDate)) {
          errors.push('Duplicate date in file');
          fieldErrors.date = 'Duplicate';
        }
        seenDatesInFile.add(normalizedDate);
      }
    }

    const normalizedType = normalizeHolidayType(row.type);
    if (normalizedType === null) {
      errors.push('Type must be Public Holiday or Optional Holiday');
      fieldErrors.type = 'Invalid';
    }

    if (
      row.description &&
      row.description.length > HOLIDAY_DESCRIPTION_MAX_LENGTH
    ) {
      errors.push(
        `Description exceeds ${HOLIDAY_DESCRIPTION_MAX_LENGTH} characters`
      );
      fieldErrors.description = 'Too long';
    }

    return {
      ...row,
      date: normalizedDate,
      type: normalizedType || holidayTypes.public,
      _errors: errors,
      _fieldErrors: fieldErrors,
    };
  });
};

/**
 * Parses a CSV/XLSX/XLS file into holiday row objects using
 * `HOLIDAY_HEADER_ALIASES` to recognize common header spellings. Does not
 * validate — pair with `validateHolidayRows`.
 */
export const parseHolidayFileToRows = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, {
          type: 'binary',
          cellDates: true,
        });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: '',
          raw: false,
          dateNF: 'yyyy-mm-dd',
        });

        if (!raw.length) {
          return reject(new Error('The file appears to be empty.'));
        }

        const headers = raw[0].map(
          (h) =>
            HOLIDAY_HEADER_ALIASES[String(h).trim().toLowerCase()] ||
            String(h).trim().toLowerCase()
        );

        const dataRows = raw
          .slice(1)
          .filter((row) => row.some((cell) => String(cell).trim() !== ''));

        const holidayRows = dataRows.map((row) => {
          const holiday = makeEmptyHolidayRow();
          headers.forEach((key, i) => {
            if (key in holiday) holiday[key] = String(row[i] ?? '').trim();
          });
          return holiday;
        });

        resolve(holidayRows);
      } catch {
        reject(
          new Error(
            'Could not parse the file. Please check it is a valid CSV or Excel file.'
          )
        );
      }
    };
    reader.onerror = () => reject(new Error('Failed to read the file.'));
    reader.readAsBinaryString(file);
  });
